class StoriesCarousel extends HTMLElement {
  constructor () {
    super()
    this.currentSlide = 0
    this.totalSlides = 0
    this.timerId = null
    this.isTransitioning = false
    this.isPaused = false
    this.progress = 0
    this.startTs = 0
    this.rafId = null
    this.animationTimers = []
    this.delay = this.resolveDelay()

    // Touch/swipe properties
    this.touchStartX = 0
    this.touchStartY = 0
    this.touchEndX = 0
    this.touchEndY = 0
    this.isDragging = false
    this.minSwipeDistance = 50

    // Bind methods
    this.boundOnMouseEnter = this.onMouseEnter.bind(this)
    this.boundOnMouseLeave = this.onMouseLeave.bind(this)
    this.boundOnTouchStart = this.onTouchStart.bind(this)
    this.boundOnTouchMove = this.onTouchMove.bind(this)
    this.boundOnTouchEnd = this.onTouchEnd.bind(this)
    this.boundOnMouseDown = this.onMouseDown.bind(this)
    this.boundOnMouseMove = this.onMouseMove.bind(this)
    this.boundOnMouseUp = this.onMouseUp.bind(this)
  }

  connectedCallback () {
    this.init()
    this.addEventListener('mouseenter', this.boundOnMouseEnter)
    this.addEventListener('mouseleave', this.boundOnMouseLeave)

    // Touch events
    this.addEventListener('touchstart', this.boundOnTouchStart, { passive: true })
    this.addEventListener('touchmove', this.boundOnTouchMove, { passive: true })
    this.addEventListener('touchend', this.boundOnTouchEnd, { passive: true })

    // Mouse drag events
    this.addEventListener('mousedown', this.boundOnMouseDown)
    this.addEventListener('mousemove', this.boundOnMouseMove)
    this.addEventListener('mouseup', this.boundOnMouseUp)
    document.addEventListener('mouseup', this.boundOnMouseUp)
  }

  disconnectedCallback () {
    this.removeEventListener('mouseenter', this.boundOnMouseEnter)
    this.removeEventListener('mouseleave', this.boundOnMouseLeave)

    // Touch events
    this.removeEventListener('touchstart', this.boundOnTouchStart)
    this.removeEventListener('touchmove', this.boundOnTouchMove)
    this.removeEventListener('touchend', this.boundOnTouchEnd)

    // Mouse drag events
    this.removeEventListener('mousedown', this.boundOnMouseDown)
    this.removeEventListener('mousemove', this.boundOnMouseMove)
    this.removeEventListener('mouseup', this.boundOnMouseUp)
    document.removeEventListener('mouseup', this.boundOnMouseUp)

    this.destroy()
  }

  init () {
    this.setupSlides()
    this.initProgress()
    this.bindProgressButtons()
    this.startInitialAnimation()
    this.startAutoplay()
  }

  destroy () {
    this.clearAllTimers()
    this.cancelRaf()
  }

  resolveDelay () {
    const k = (this.dataset.delay || 'medium').toLowerCase()
    if (k === 'slow') return 5000
    if (k === 'fast') return 1500
    return 3000
  }

  get titleDelay () {
    const v = parseInt(this.dataset.titleDelay || '200', 10)
    return Number.isFinite(v) ? v : 200
  }

  get titleDuration () {
    const v = parseInt(this.dataset.titleDuration || '500', 10)
    return Number.isFinite(v) ? v : 500
  }

  get metaDelay () {
    const v = parseInt(this.dataset.metaDelay || '400', 10)
    return Number.isFinite(v) ? v : 400
  }

  get metaDuration () {
    const v = parseInt(this.dataset.metaDuration || '300', 10)
    return Number.isFinite(v) ? v : 300
  }

  get fadeDuration () {
    const v = parseInt(this.dataset.fadeDuration || '400', 10)
    return Number.isFinite(v) ? v : 400
  }

  get slides () {
    return this.querySelectorAll('.customer-stories__slide')
  }

  get progressLines () {
    return this.querySelectorAll('.customer-stories__progress-line')
  }

  get progressFills () {
    return this.querySelectorAll('.customer-stories__progress-fill')
  }

  get progressGlows () {
    return this.querySelectorAll('.customer-stories__progress-glow')
  }

  setupSlides () {
    this.totalSlides = this.slides.length

    this.slides.forEach((slide, index) => {
      // Split title text into words for animation (only if not already done)
      const title = slide.querySelector('.customer-stories__title')
      if (title && !title.dataset.split) {
        this.splitTitleIntoWords(title)
        title.dataset.split = 'true'
      }
    })
  }

  splitTitleIntoWords (titleEl) {
    const text = titleEl.textContent.trim()
    titleEl.textContent = ''
    const parts = text.split(/(\s+)/)

    parts.forEach((part) => {
      if (part.trim() === '') {
        titleEl.appendChild(document.createTextNode(part))
      } else {
        const wrap = document.createElement('span')
        wrap.className = 'word-wrap'
        const inner = document.createElement('span')
        inner.className = 'word-inner'
        inner.textContent = part
        wrap.appendChild(inner)
        titleEl.appendChild(wrap)
      }
    })

    // Make title visible now that it's split
    titleEl.classList.add('title-split')
  }

  initProgress () {
    this.progressFills.forEach((fill, index) => {
      fill.style.setProperty('--progress-percent', '0')
    })

    this.progressGlows.forEach((glow, index) => {
      if (index === 0) {
        // First bar starts with glow visible
        glow.classList.remove('progress-complete')
      } else {
        // Other bars start with glow hidden
        glow.classList.add('progress-complete')
      }
    })
  }

  bindProgressButtons () {
    this.progressLines.forEach((btn, index) => {
      btn.addEventListener('click', () => {
        if (this.isTransitioning) return // Block navigation during transitions
        this.goToSlide(index)
      })
    })
  }

  startInitialAnimation () {
    // Animate in the first slide's elements
    this.animateSlideElementsIn(0)
  }

  startAutoplay () {
    this.cancelRaf()
    this.startTs = performance.now()

    const tick = (ts) => {
      if (this.isPaused || this.isTransitioning) {
        this.startTs = ts - this.progress * this.delay
        this.rafId = requestAnimationFrame(tick)
        return
      }

      this.progress = Math.min(1, (ts - this.startTs) / this.delay)
      const progressPercent = this.progress * 100

      const fill = this.progressFills[this.currentSlide]
      const glow = this.progressGlows[this.currentSlide]

      if (fill) fill.style.setProperty('--progress-percent', progressPercent.toString())

      // Hide glow when progress is complete, show when progressing
      if (glow) {
        if (this.progress >= 1) {
          glow.classList.add('progress-complete')
        } else {
          glow.classList.remove('progress-complete')
        }
      }

      if (this.progress >= 1) {
        this.nextSlide()
        this.progress = 0
        this.startTs = performance.now()
      }

      this.rafId = requestAnimationFrame(tick)
    }

    this.rafId = requestAnimationFrame(tick)
  }

  nextSlide () {
    const nextIndex = (this.currentSlide + 1) % this.totalSlides
    this.goToSlide(nextIndex)
  }

  goToSlide (targetIndex) {
    if (targetIndex === this.currentSlide || this.isTransitioning) return

    this.isTransitioning = true

    // Update progress bars
    this.updateProgressBars(targetIndex)

    // Ensure target slide is in hidden state before making visible
    // (either from previous fadeOut or initial state)
    this.resetSlideToInitialState(targetIndex)

    // Make target slide visible underneath current slide
    const targetSlide = this.slides[targetIndex]
    if (targetSlide) {
      targetSlide.classList.remove('slide-hidden')
      targetSlide.classList.add('slide-visible')
    }

    // Fade out current slide
    this.fadeOutSlide(this.currentSlide, () => {
      // Update current slide index
      this.currentSlide = targetIndex

      // Animate in new slide's elements (target slide already reset and visible)
      this.animateSlideElementsIn(targetIndex)

      // Allow transitions again
      this.isTransitioning = false

      // Reset progress
      this.progress = 0
      this.startTs = performance.now()
    })
  }

  fadeOutSlide (slideIndex, callback) {
    const slide = this.slides[slideIndex]
    if (!slide) {
      callback && callback()
      return
    }

    // IMMEDIATELY reset slide elements to hidden state (so no reset animation is visible when returning later)
    this.resetSlideToInitialState(slideIndex)

    // Add slide-hidden class to fade out
    slide.classList.remove('slide-visible')
    slide.classList.add('slide-hidden')

    // Execute callback after fade completes
    this.animationTimers.push(setTimeout(() => {
      callback && callback()
    }, this.fadeDuration))
  }

  resetSlideToInitialState (slideIndex) {
    const slide = this.slides[slideIndex]
    if (!slide) return

    const badge = slide.querySelector('.customer-stories__badge')
    const link = slide.querySelector('.customer-stories__link')
    const title = slide.querySelector('.customer-stories__title')

    // Reset all elements to hidden state
    if (badge) {
      badge.classList.remove('badge-visible')
      badge.classList.add('badge-hidden')
    }

    if (link) {
      link.classList.remove('link-visible')
      link.classList.add('link-hidden')
    }

    if (title) {
      // For title, remove transition temporarily to avoid visible animation
      const wordInners = title.querySelectorAll('.word-inner')
      wordInners.forEach(w => {
        w.style.transition = 'none'
      })

      title.classList.remove('title-visible')
      title.classList.add('title-hidden')

      // Force reflow, then restore transitions
      title.getBoundingClientRect()

      wordInners.forEach(w => {
        w.style.transition = `transform ${this.titleDuration}ms ease`
      })
    }
  }

  animateSlideElementsIn (slideIndex) {
    const slide = this.slides[slideIndex]
    if (!slide) return

    const title = slide.querySelector('.customer-stories__title')
    const badge = slide.querySelector('.customer-stories__badge')
    const link = slide.querySelector('.customer-stories__link')

    // Animate title first
    this.animationTimers.push(setTimeout(() => {
      if (title) {
        title.classList.remove('title-hidden')
        title.classList.add('title-visible')
      }

      // Animate badge and link after meta delay
      this.animationTimers.push(setTimeout(() => {
        if (badge) {
          badge.classList.remove('badge-hidden')
          badge.classList.add('badge-visible')
        }

        if (link) {
          link.classList.remove('link-hidden')
          link.classList.add('link-visible')
        }
      }, this.metaDelay))
    }, this.titleDelay))
  }

  updateProgressBars (targetIndex) {
    this.progressFills.forEach((fill, index) => {
      const progressPercent = index < targetIndex ? '100' : '0'
      fill.style.setProperty('--progress-percent', progressPercent)
    })

    this.progressGlows.forEach((glow, index) => {
      if (index < targetIndex) {
        // Completed bars - hide glow
        glow.classList.add('progress-complete')
      } else if (index === targetIndex) {
        // Current bar - show glow (will be controlled by animation)
        glow.classList.remove('progress-complete')
      } else {
        // Future bars - hide glow
        glow.classList.add('progress-complete')
      }
    })
  }

  onMouseEnter () {
    this.isPaused = true
  }

  onMouseLeave () {
    this.isPaused = false
  }

  clearAllTimers () {
    this.animationTimers.forEach(timer => clearTimeout(timer))
    this.animationTimers = []

    if (this.timerId) {
      clearTimeout(this.timerId)
      this.timerId = null
    }
  }

  cancelRaf () {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  // Touch event handlers
  onTouchStart (e) {
    if (e.touches.length > 1) return // Ignore multi-touch
    const touch = e.touches[0]
    this.touchStartX = touch.clientX
    this.touchStartY = touch.clientY
    this.touchEndX = touch.clientX
    this.touchEndY = touch.clientY
  }

  onTouchMove (e) {
    if (e.touches.length > 1) return // Ignore multi-touch
    const touch = e.touches[0]
    this.touchEndX = touch.clientX
    this.touchEndY = touch.clientY
  }

  onTouchEnd () {
    this.handleSwipeGesture()
  }

  // Mouse drag handlers
  onMouseDown (e) {
    this.isDragging = true
    this.touchStartX = e.clientX
    this.touchStartY = e.clientY
    this.touchEndX = e.clientX
    this.touchEndY = e.clientY
    e.preventDefault() // Prevent text selection
  }

  onMouseMove (e) {
    if (!this.isDragging) return
    this.touchEndX = e.clientX
    this.touchEndY = e.clientY
  }

  onMouseUp () {
    if (!this.isDragging) return
    this.isDragging = false
    this.handleSwipeGesture()
  }

  handleSwipeGesture () {
    const deltaX = this.touchEndX - this.touchStartX
    const deltaY = this.touchEndY - this.touchStartY
    const absX = Math.abs(deltaX)
    const absY = Math.abs(deltaY)

    // Only process horizontal swipes that are longer than vertical movement
    if (absX < this.minSwipeDistance || absY > absX) return

    // Don't process during transitions
    if (this.isTransitioning) return

    if (deltaX > 0) {
      // Swipe right - go to previous slide
      const prevIndex = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides
      this.goToSlide(prevIndex)
    } else {
      // Swipe left - go to next slide
      const nextIndex = (this.currentSlide + 1) % this.totalSlides
      this.goToSlide(nextIndex)
    }
  }
}

if (!customElements.get('stories-carousel')) {
  customElements.define('stories-carousel', StoriesCarousel)
}
